import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { T } from '@salecycle/experimental/Typography/Typography';
import { Alert, Input, SingleDatePicker } from '@salecycle/ui-component-library';
import { useSetSendDate } from '../../state/actions/useSetSendDate';
import { useMemo, useState } from 'react';
import { useCampaignEditorContext } from '../../state/hooks/useCampaignEditorContext';
import { OffsiteCampaignStatus } from 'models';

const Row = styled.div`
  display: flex;
  gap: 12px;
`;

export const SchedulingTab = () => {
  const { t } = useTranslation('campaigns');
  const [isFocused, setFocused] = useState(false);
  const { state } = useCampaignEditorContext();
  const [date, setSendDate] = useSetSendDate();

  const isToday = useMemo(() => {
    if (!date) {
      return true;
    }
    return date.isSame(new Date(), 'day');
  }, [date]);

  const initialVisibleMonth = useMemo(() => {
    return () => {
      // Prioritise showing the month containing the earliest available date
      if (state.campaign.minSendDate) {
        return state.campaign.minSendDate;
      }
      // Fall back to selected date if available
      if (date) {
        return date;
      }
      // Otherwise default to today
      return moment();
    };
  }, [state.campaign.minSendDate, date]);

  return (
    <>
      <T variant="headingH1">{t('bulkRetargetingCampaigns.editor.tabs.scheduling')}</T>
      <Row>
        <SingleDatePicker
          date={date}
          isFutureDatesOnly
          focused={isFocused}
          onFocusChange={({ focused }) => setFocused(focused)}
          label={t('bulkRetargetingCampaigns.editor.scheduling.date.label')}
          placeholder={t('bulkRetargetingCampaigns.editor.scheduling.date.placeholder')}
          spacingBelow={false}
          minDate={state.campaign.minSendDate}
          maxDate={state.campaign.maxSendDate}
          initialVisibleMonth={initialVisibleMonth}
          onDateChange={updatedDate => {
            if (!date || !updatedDate) {
              setSendDate(updatedDate);
              return;
            }
            const hour = date?.hour() ?? 12;
            const minute = date?.minute() || 0;
            setSendDate(updatedDate.hour(hour).minute(minute));
          }}
          disabled={state?.readOnly}
        />
        {date && (
          <div>
            <Input
              label={t('bulkRetargetingCampaigns.editor.scheduling.time.label')}
              type="time"
              spacingBelow={false}
              onChange={event => {
                if (!date) {
                  return;
                }
                const [hour, minute] = event.split(':');
                const updatedDate = date
                  .clone()
                  .hour(parseInt(hour, 10))
                  .minute(parseInt(minute, 10));
                setSendDate(updatedDate);
              }}
              pattern="([01]?[0-9]|2[0-3]):[0-5][0-9]"
              value={date?.format('HH:mm')}
              disabled={state?.readOnly}
            />
          </div>
        )}
      </Row>
      {isToday && state.status === OffsiteCampaignStatus.Draft && (
        <Alert id="SchedulingNotice" heading={t('bulkRetargetingCampaigns.editor.scheduling.notice.heading')}>
          <p>{t('bulkRetargetingCampaigns.editor.scheduling.notice.body')}</p>
        </Alert>
      )}
    </>
  );
};
